import React, { useEffect, useState, useCallback } from 'react';
import { toast } from 'react-hot-toast';
import { Plus, Edit2, Key, UserX, Building2, Users as UsersIcon, Trash2, AlertTriangle } from 'lucide-react';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { PageHeader, Button, Card, Modal, FormField, Badge, RoleBadge, LoadingSpinner, Table, SearchBar, Tabs } from '../components/UI';
import { format, parseISO } from 'date-fns';

const EMPTY_USER = { username: '', email: '', full_name: '', role: 'employee', department_id: '', password: '', must_change_password: true };
const EMPTY_DEPT = { name: '', description: '' };

export default function Users() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [depts, setDepts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [tab, setTab] = useState('users');
  const [userModal, setUserModal] = useState(false);
  const [deptModal, setDeptModal] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [form, setForm] = useState(EMPTY_USER);
  const [deptForm, setDeptForm] = useState(EMPTY_DEPT);
  const [saving, setSaving] = useState(false);
  const [resetResult, setResetResult] = useState(null);
  const [errors, setErrors] = useState({});
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [u, d] = await Promise.all([api.get('/users'), api.get('/users/departments')]);
      setUsers(u.data); setDepts(d.data);
    } catch(e) { toast.error('Failed to load users'); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  function openCreate() { setEditUser(null); setForm(EMPTY_USER); setErrors({}); setUserModal(true); }
  function openEdit(u) {
    setEditUser(u);
    setForm({ username: u.username, email: u.email, full_name: u.full_name || '', role: u.role, department_id: u.department_id || '', password: '', must_change_password: !!u.must_change_password });
    setErrors({}); setUserModal(true);
  }

  function set(k, v) { setForm(f => ({ ...f, [k]: v })); setErrors(e => ({ ...e, [k]: '' })); }

  async function saveUser(e) {
    e.preventDefault();
    const errs = {};
    if (!form.username.trim()) errs.username = 'Username required.';
    if (!form.full_name.trim()) errs.full_name = 'Full name required.';
    if (!form.email.trim()) errs.email = 'Email required.';
    if (!editUser && (!form.password || form.password.length < 12)) errs.password = 'Password must be at least 12 characters.';
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setSaving(true);
    try {
      if (editUser) {
        await api.put(`/users/${editUser.id}`, { username: form.username, full_name: form.full_name, email: form.email, role: form.role, department_id: form.department_id || null, is_active: 1 });
        toast.success('User updated.');
      } else {
        await api.post('/users', form);
        toast.success('User created.');
      }
      setUserModal(false); load();
    } catch(err) { toast.error(err.response?.data?.error || 'Save failed.'); }
    setSaving(false);
  }

  async function resetPassword(userId) {
    try {
      const res = await api.post(`/users/${userId}/reset-password`);
      setResetResult(res.data.temp_password);
      toast.success('Password reset.');
    } catch { toast.error('Reset failed.'); }
  }

  async function deactivate(u) {
    if (!window.confirm(`Deactivate ${u.full_name || u.username}?`)) return;
    try { await api.delete(`/users/${u.id}`); toast.success('User deactivated.'); load(); }
    catch { toast.error('Failed.'); }
  }

  async function permanentlyDelete() {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/users/${deleteTarget.id}/permanent`);
      toast.success('User permanently deleted.');
      setDeleteTarget(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Delete failed.');
    }
    setDeleting(false);
  }

  async function saveDept(e) {
    e.preventDefault();
    if (!deptForm.name.trim()) { toast.error('Department name required.'); return; }
    setSaving(true);
    try {
      await api.post('/users/departments', deptForm);
      toast.success('Department created.'); setDeptModal(false); setDeptForm(EMPTY_DEPT); load();
    } catch(err) { toast.error(err.response?.data?.error || 'Failed.'); }
    setSaving(false);
  }

  const filtered = users.filter(u =>
    !search || u.full_name?.toLowerCase().includes(search.toLowerCase()) ||
    u.username.toLowerCase().includes(search.toLowerCase()) ||
    u.email.toLowerCase().includes(search.toLowerCase())
  );

  const userColumns = [
    {
      header: 'User', key: 'full_name',
      render: (v, row) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 34, height: 34, borderRadius: 9, background: 'var(--primary-ghost)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, color: 'var(--primary)', flexShrink: 0 }}>
            {(row.full_name || row.username || '?').slice(0, 2).toUpperCase()}
          </div>
          <div>
            <div style={{ fontWeight: 700, fontSize: 13.5, color: 'var(--text-primary)' }}>{row.full_name || row.username}</div>
            <div style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>{row.email}</div>
          </div>
        </div>
      )
    },
    { header: 'Username', key: 'username', render: v => <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12.5, color: 'var(--text-secondary)' }}>{v}</span> },
    { header: 'Role', key: 'role', render: v => <RoleBadge role={v} /> },
    { header: 'Department', key: 'department_name', render: v => v || <span style={{ color: 'var(--text-subtle)', fontSize: 12 }}>—</span> },
    { header: 'Last Login', key: 'last_login', render: v => v ? <span style={{ fontSize: 12.5 }}>{format(parseISO(v), 'MMM d, yyyy')}</span> : <span style={{ color: 'var(--text-subtle)', fontSize: 12 }}>Never</span> },
    { header: 'Status', key: 'is_active', render: v => <Badge label={v ? 'Active' : 'Inactive'} type={v ? 'active' : 'inactive'} /> },
    {
      header: 'Actions', key: 'id',
      render: (v, row) => (
        <div style={{ display: 'flex', gap: 5 }}>
          <button title="Edit" onClick={e => { e.stopPropagation(); openEdit(row); }} style={{ background: 'var(--bg-elevated)', border: 'none', borderRadius: 7, padding: '5px 8px', cursor: 'pointer', color: 'var(--text-secondary)' }}><Edit2 size={13} /></button>
          <button title="Reset Password" onClick={e => { e.stopPropagation(); resetPassword(row.id); }} style={{ background: 'var(--bg-elevated)', border: 'none', borderRadius: 7, padding: '5px 8px', cursor: 'pointer', color: 'var(--warning)' }}><Key size={13} /></button>
          {row.id !== me?.id && <button title="Deactivate" onClick={e => { e.stopPropagation(); deactivate(row); }} style={{ background: '#fee2e2', border: 'none', borderRadius: 7, padding: '5px 8px', cursor: 'pointer', color: 'var(--danger)' }}><UserX size={13} /></button>}
          {row.id !== me?.id && <button title="Delete Permanently" onClick={e => { e.stopPropagation(); setDeleteTarget(row); }} style={{ background: '#fee2e2', border: 'none', borderRadius: 7, padding: '5px 8px', cursor: 'pointer', color: 'var(--danger)' }}><Trash2 size={13} /></button>}
        </div>
      )
    },
  ];

  const deptColumns = [
    { header: 'Name', key: 'name', render: v => <strong style={{ fontSize: 13.5 }}>{v}</strong> },
    { header: 'Description', key: 'description', render: v => v || <span style={{ color: 'var(--text-subtle)', fontSize: 12 }}>—</span> },
    { header: 'Created', key: 'created_at', render: v => v ? format(parseISO(v), 'MMM d, yyyy') : '—' },
  ];

  return (
    <div style={{ padding: '32px 36px', maxWidth: 1200, margin: '0 auto' }} className="fade-in">
      <PageHeader
        title="User Management"
        subtitle="Manage users, roles, departments and permissions"
        action={
          <div style={{ display: 'flex', gap: 10 }}>
            <Button variant="secondary" icon={Building2} onClick={() => { setDeptForm(EMPTY_DEPT); setDeptModal(true); }}>New Department</Button>
            <Button icon={Plus} onClick={openCreate}>New User</Button>
          </div>
        }
      />

      <Tabs
        tabs={[
          { id: 'users', label: 'Users', icon: UsersIcon, count: users.length },
          { id: 'departments', label: 'Departments', icon: Building2, count: depts.length },
        ]}
        active={tab} onChange={setTab}
      />

      {tab === 'users' && (
        <>
          <Card style={{ padding: '14px 18px', marginBottom: 16 }}>
            <SearchBar value={search} onChange={setSearch} placeholder="Search by name, username, email…" />
          </Card>
          <Card style={{ padding: 0 }}>
            {loading ? <LoadingSpinner /> : <Table columns={userColumns} data={filtered} emptyMessage="No users found." />}
          </Card>
        </>
      )}

      {tab === 'departments' && (
        <Card style={{ padding: 0 }}>
          {loading ? <LoadingSpinner /> : <Table columns={deptColumns} data={depts} emptyMessage="No departments found." />}
        </Card>
      )}

      {/* Create/Edit User Modal */}
      <Modal open={userModal} onClose={() => setUserModal(false)} title={editUser ? 'Edit User' : 'Create User'} size="md"
        footer={<>
          <Button variant="secondary" onClick={() => setUserModal(false)}>Cancel</Button>
          <Button loading={saving} onClick={saveUser} icon={editUser ? null : Plus}>{editUser ? 'Save Changes' : 'Create User'}</Button>
        </>}
      >
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <FormField label="Username" required error={errors.username} style={{ gridColumn: '1/-1' }}>
            <input value={form.username} onChange={e => set('username', e.target.value)} placeholder="username" />
          </FormField>
          <FormField label="Full Name" required error={errors.full_name}>
            <input value={form.full_name} onChange={e => set('full_name', e.target.value)} placeholder="Jane Doe" />
          </FormField>
          <FormField label="Email" required error={errors.email}>
            <input type="email" value={form.email} onChange={e => set('email', e.target.value)} placeholder="jane@org.com" />
          </FormField>
          <FormField label="Role" required>
            <select value={form.role} onChange={e => set('role', e.target.value)}>
              <option value="employee">Employee</option>
              <option value="supervisor">Supervisor</option>
              <option value="manager">Manager</option>
              <option value="admin">Admin</option>
            </select>
          </FormField>
          <FormField label="Department">
            <select value={form.department_id} onChange={e => set('department_id', e.target.value)}>
              <option value="">No department</option>
              {depts.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
          </FormField>
          {!editUser && (
            <>
              <FormField label="Password" required error={errors.password} hint="Min 12 chars, uppercase, number, symbol">
                <input type="password" value={form.password} onChange={e => set('password', e.target.value)} placeholder="Secure password" />
              </FormField>
              <FormField label="Force Password Change">
                <select value={form.must_change_password ? '1' : '0'} onChange={e => set('must_change_password', e.target.value === '1')}>
                  <option value="1">Yes – user must change on first login</option>
                  <option value="0">No</option>
                </select>
              </FormField>
            </>
          )}
        </div>
      </Modal>

      {/* Dept Modal */}
      <Modal open={deptModal} onClose={() => setDeptModal(false)} title="Create Department" size="sm"
        footer={<>
          <Button variant="secondary" onClick={() => setDeptModal(false)}>Cancel</Button>
          <Button loading={saving} onClick={saveDept} icon={Plus}>Create</Button>
        </>}
      >
        <FormField label="Department Name" required>
          <input value={deptForm.name} onChange={e => setDeptForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Finance" />
        </FormField>
        <FormField label="Description">
          <textarea value={deptForm.description} onChange={e => setDeptForm(f => ({ ...f, description: e.target.value }))} placeholder="Brief description…" rows={2} />
        </FormField>
      </Modal>

      {/* Password Reset Result */}
      <Modal open={!!resetResult} onClose={() => setResetResult(null)} title="Password Reset" size="sm"
        footer={<Button onClick={() => setResetResult(null)}>Close</Button>}
      >
        <p style={{ fontSize: 13.5, color: 'var(--text-secondary)', marginBottom: 14 }}>The temporary password has been set. Share this securely with the user:</p>
        <div style={{ padding: '14px 18px', background: 'var(--primary-ghost)', borderRadius: 10, fontFamily: 'var(--font-mono)', fontSize: 16, fontWeight: 700, color: 'var(--primary)', textAlign: 'center', letterSpacing: '0.1em', border: '1.5px solid var(--primary)', userSelect: 'all' }}>
          {resetResult}
        </div>
        <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 12 }}>The user will be required to change this password on their next login.</p>
      </Modal>

      {/* Permanent Delete Confirmation */}
      <Modal open={!!deleteTarget} onClose={() => setDeleteTarget(null)} title="Delete User Permanently" size="sm"
        footer={<>
          <Button variant="secondary" onClick={() => setDeleteTarget(null)} disabled={deleting}>Cancel</Button>
          <Button variant="danger" loading={deleting} onClick={permanentlyDelete} icon={Trash2}>Delete Permanently</Button>
        </>}
      >
        <div style={{ display: 'flex', gap: 12, padding: '12px 14px', background: '#fef2f2', borderRadius: 10, border: '1.5px solid rgba(220,38,38,.2)' }}>
          <AlertTriangle size={18} color="var(--danger)" style={{ flexShrink: 0, marginTop: 1 }} />
          <p style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.6 }}>
            This will permanently remove <strong>{deleteTarget?.full_name || deleteTarget?.username}</strong> ({deleteTarget?.username}) from the system.
            This cannot be undone. If this user has submitted reports or other linked records, deletion may be blocked — deactivate the account instead.
          </p>
        </div>
      </Modal>
    </div>
  );
}
